import React from 'react'
import { useTranslate } from 'src/localization/Trns'
import { ReceiptAttachmentModel } from 'src/models/receiptAttachment.models'
import { ConnectedPlatformStatus } from 'src/models/transaction.models'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'
import { css } from 'styled-components'

import style from './AttachmentListRows.style'
import AttachmentRow from './AttachmentRow'
import { statusForAttachment, statusForRow } from './statusForRow'
import WideDropzone from './WideDropzone'

interface AttachmentListRowsProps {
  attachments: ReceiptAttachmentModel[]
  uploadingAttachmentThumbnails?: Record<string, string>
  uploadAttachments?: (attachments: PostReceiptAttachmentRequest[]) => void
  deleteAttachment?: (attachmentId: string) => void
  setAttachmentError?: (
    errorMsg: string | React.ReactElement | undefined,
  ) => void
  setErrors?: (onlyOneReceipt: boolean) => void
  integrationName?: string
  integrationLogoSrc?: string
  erpStatusByAttachmentId?: Record<string, ConnectedPlatformStatus>
}

const uploadingRowStyle = css`
  display: flex;
  align-items: center;
  padding: 8px 0;
  gap: 16px;

  .uploading-thumb {
    width: 40px;
    height: 40px;
    border-radius: 4px;
    object-fit: cover;
    opacity: 0.6;
  }

  .uploading-text {
    display: flex;
    flex-direction: column;
    gap: 0;
    min-width: 0;
    font-family: Rubik, sans-serif;

    .filename {
      font-size: 14px;
      line-height: 18px;
      color: #1e2c4c;
    }

    .meta {
      font-size: 12px;
      line-height: 16px;
      color: #697080;
    }

    .progress {
      width: 200px;
      height: 4px;
      border-radius: 4px;
      overflow: hidden;
      background: rgba(218, 219, 224, 0.6);
      margin-top: 6px;
    }

    .progress-fill {
      height: 100%;
      background: #4489f9;
      width: 40%;
      border-radius: 4px;
      animation: attachment-row-indeterminate 1.2s ease-in-out infinite
        alternate;
    }
  }
`

function UploadingRow({
  thumbnailUrl,
}: {
  thumbnailUrl: string
}): React.ReactElement {
  const translate = useTranslate()
  const loadingLabel = translate('Loading')
  return (
    <div css={uploadingRowStyle} data-testid="attachmentRow-uploading">
      <img className="uploading-thumb" src={thumbnailUrl} alt="" />
      <div className="uploading-text">
        <span className="filename">{translate('Receipt')}</span>
        <span className="meta">{loadingLabel}</span>
        <div className="progress" role="progressbar" aria-label={loadingLabel}>
          <div className="progress-fill" />
        </div>
      </div>
    </div>
  )
}

export default function AttachmentListRows(
  props: AttachmentListRowsProps,
): React.ReactElement {
  const {
    attachments,
    uploadingAttachmentThumbnails,
    uploadAttachments,
    deleteAttachment,
    setAttachmentError,
    setErrors,
    integrationName,
    integrationLogoSrc,
    erpStatusByAttachmentId,
  } = props

  const uploadingEntries = Object.entries(uploadingAttachmentThumbnails ?? {})

  return (
    <div
      css={style}
      className="AttachmentListRows"
      data-testid="attachmentListRows"
    >
      {uploadAttachments && (
        <WideDropzone
          uploadAttachments={uploadAttachments}
          setAttachmentError={setAttachmentError}
          setErrors={setErrors}
          attachmentsLength={attachments.length}
        />
      )}
      {uploadingEntries.map(([uploadId, thumbnailUrl]) => (
        <UploadingRow key={uploadId} thumbnailUrl={thumbnailUrl} />
      ))}
      {attachments.map((attachment) => {
        const erpStatus = erpStatusByAttachmentId?.[attachment.id]
        const rowStatus = statusForAttachment(attachment, {
          erpStatus,
          erpIntegrationName: integrationName,
        })
        const skipRealStatus =
          rowStatus.statusLabel === 'Complete' &&
          !attachment.verification &&
          !erpStatus
        const finalStatus = skipRealStatus
          ? statusForRow({ uploadType: attachment.uploadType })
          : rowStatus
        return (
          <AttachmentRow
            key={attachment.id}
            attachment={attachment}
            status={finalStatus}
            integrationName={integrationName}
            integrationLogoSrc={integrationLogoSrc}
            deleteAttachment={deleteAttachment}
          />
        )
      })}
    </div>
  )
}
